<template>
  <div>
    <!-- Filter Section -->
    <FilterReleasePatients
      :loading="loading"
      @searchFilter="searchFilter"
      @filterPage="filterPage"
      @exportExcel="exportExcel"
    />

    <!-- Main Content Card -->
    <b-card>
      <TitleTable titleTable="Release Patients" iconTable="fas fa-sign-out-alt" />
      
      <!-- Controls Section -->
      <div class="m-2">
        <b-row>
          <b-col cols="12" md="6" class="d-flex align-items-center justify-content-start mb-1 mb-md-0">
            <treeselect
              v-model="perPage"
              :options="perPageOptions"
              :clearable="false"
              class="w-25"
              @input="onPerPageChange"
            />
          </b-col>
          <b-col cols="12" md="6">
            <div class="d-flex align-items-center justify-content-end">
              <b-button
                variant="outline-secondary"
                @click="refreshData"
                :disabled="loading"
                class="mr-2"
              >
                <i class="fas fa-sync-alt"></i>
                <span v-if="!loading">Refresh</span>
                <span v-else><SpinnerLoading /></span>
              </b-button>
            </div>
          </b-col>
        </b-row>
      </div>

      <!-- Patients Table -->
      <b-table
        :items="patients"
        :fields="tableFields"
        hover
        bordered
        responsive
        primary-key="id"
        class="position-relative min-height-table"
        show-empty
        empty-text="No patients found"
        :busy="loading"
      >
        <template #table-busy>
          <div class="text-center text-primary my-2">
            <b-spinner class="align-middle mr-2"></b-spinner>
            <strong>Loading patients...</strong>
          </div>
        </template>

        <!-- Patient Column -->
        <template #cell(patient)="data">
          <b-media vertical-align="center">
            <div>
              <span class="font-weight-bold d-block text-nowrap">
                {{ data.item.full_name }}
              </span>
              <small class="text-muted">
                {{ data.item.email || 'No email' }}
              </small>
            </div>
          </b-media>
        </template>

        <!-- Phone Column -->
        <template #cell(phone)="data">
          <div class="text-nowrap">
            <span v-if="data.item.phone">
              {{ formatPhone(data.item.phone) }}
            </span>
            <span v-else class="text-muted">-</span>
          </div>
        </template>

        <!-- DOB Column -->
        <template #cell(date_birth)="data">
          <div class="text-nowrap">
            <span v-if="data.item.date_birth">
              {{ formatDate(data.item.date_birth) }}
            </span>
            <span v-else class="text-muted">-</span>
          </div>
        </template>

        <!-- Visit Count Column -->
        <template #cell(visit_count)="data">
          <div class="text-center">
            <b-badge variant="info" class="badge-glow">
              {{ data.item.visit_count || 0 }}
            </b-badge>
          </div>
        </template>

                 <!-- Patient Type Column -->
         <template #cell(patient_type)="data">
           <div class="text-nowrap">
             <b-badge 
               :variant="data.item.patient_type === 'insurance' ? 'success' : 'primary'"
               class="badge-glow"
             >
               {{ data.item.patient_type === 'insurance' ? 'Insurance' : 'Cash' }}
             </b-badge>
           </div>
         </template>

        <!-- Has Insurance Column -->
        <template #cell(has_insurance)="data">
          <div class="text-center">
            <i
              :class="data.item.has_insurance === 'true' || data.item.patient_type === 'insurance' 
                ? 'fas fa-shield-alt text-success' 
                : 'fas fa-shield-alt text-muted'"
            ></i>
          </div>
        </template>

        <!-- Status Column -->
        <template #cell(status)="data">
          <div class="text-nowrap">
            <b-badge
              :variant="data.item.status ? 'success' : 'danger'"
              class="badge-glow"
            >
              {{ data.item.status ? 'Active' : 'Inactive' }}
            </b-badge>
          </div>
        </template>

        <!-- Actions Column -->
        <template #cell(actions)="data">
          <div class="text-nowrap">
            <b-dropdown variant="link" no-caret boundary="window" menu-class="dropdown-menu-full">
              <template #button-content>
                <i class="fas fa-ellipsis-v"></i>
              </template>
              
              <!-- View Details -->
              <b-dropdown-item @click="viewPatientDetails(data.item)">
                <i class="far fa-eye"></i>
                <span class="align-middle ml-2">View Details</span>
              </b-dropdown-item>

              <!-- Release Patient -->
              <b-dropdown-item 
                @click="showReleaseModal(data.item)"
                v-if="data.item.status"
              >
                <i class="fas fa-sign-out-alt"></i>
                <span class="align-middle ml-2">Release Patient</span>
              </b-dropdown-item>

              <!-- View History -->
              <b-dropdown-item @click="viewPatientHistory(data.item)">
                <i class="fas fa-history"></i>
                <span class="align-middle ml-2">View History</span>
              </b-dropdown-item>

              <!-- Generate Report -->
              <b-dropdown-item @click="generatePatientReport(data.item)">
                <i class="fas fa-file-pdf"></i>
                <span class="align-middle ml-2">Generate Report</span>
              </b-dropdown-item>
            </b-dropdown>
          </div>
        </template>
      </b-table>

      <!-- Pagination -->
      <PaginationTable
        :dataMeta="dataMeta"
        :totalUsers="totalPatients"
        :perPage="perPage"
        :currentPage="currentPage"
        @page-changed="pageChanged"
      />
    </b-card>

    <!-- Release Patient Modal -->
    <b-modal
      v-model="isReleaseModalActive"
      title="Release Patient"
      @ok="confirmReleasePatient"
      ok-title="Release Patient"
      ok-variant="danger"
      cancel-title="Cancel"
    >
      <div v-if="selectedPatient">
        <div class="text-center mb-4">
          <h5>Are you sure you want to release this patient?</h5>
        </div>
        
        <div class="patient-info-card">
          <div class="mb-2">
            <strong>Patient Name:</strong> 
            <span class="ml-2">{{ selectedPatient.full_name }}</span>
          </div>
          <div class="mb-2">
            <strong>Patient ID:</strong> 
            <span class="ml-2">{{ selectedPatient.id }}</span>
          </div>
          <div class="mb-2">
            <strong>Patient Type:</strong> 
            <b-badge 
              :variant="selectedPatient.patient_type === 'insurance' ? 'success' : 'primary'"
              class="ml-2"
            >
              {{ selectedPatient.patient_type === 'insurance' ? 'Insurance' : 'Cash' }}
            </b-badge>
          </div>
          <div class="mb-2">
            <strong>Phone:</strong> 
            <span class="ml-2">{{ selectedPatient.phone || 'N/A' }}</span>
          </div>
          <div class="mb-2">
            <strong>Total Visits:</strong> 
            <span class="ml-2">{{ selectedPatient.visit_count || 0 }}</span>
          </div>
        </div>

        <div class="alert alert-warning mt-3">
          <i class="fas fa-exclamation-triangle mr-2"></i>
          <strong>Warning:</strong> This action cannot be undone. The patient will be marked as released from the system.
        </div>
      </div>
    </b-modal>

    <!-- Patient Details Modal -->
    <b-modal
      v-model="isDetailsModalActive"
      title="Patient Details"
      hide-footer
      size="lg"
    >
      <div v-if="selectedPatient">
        <b-card>
          <b-card-header>
            <h5 class="mb-0">{{ selectedPatient.full_name }}</h5>
          </b-card-header>
          <b-card-body>
            <b-row>
              <b-col md="6">
                <p><strong>ID:</strong> {{ selectedPatient.id }}</p>
                <p><strong>Phone:</strong> {{ selectedPatient.phone || 'N/A' }}</p>
                <p><strong>Email:</strong> {{ selectedPatient.email || 'N/A' }}</p>
                <p><strong>Gender:</strong> {{ selectedPatient.gender || 'N/A' }}</p>
                <p><strong>Date of Birth:</strong> {{ formatDate(selectedPatient.date_birth) || 'N/A' }}</p>
              </b-col>
              <b-col md="6">
                <p><strong>Patient Type:</strong> 
                  <b-badge 
                    :variant="selectedPatient.patient_type === 'insurance' ? 'success' : 'primary'"
                  >
                    {{ selectedPatient.patient_type === 'insurance' ? 'Insurance' : 'Cash' }}
                  </b-badge>
                </p>
                <p><strong>Status:</strong> 
                  <b-badge :variant="selectedPatient.status ? 'success' : 'danger'">
                    {{ selectedPatient.status ? 'Active' : 'Inactive' }}
                  </b-badge>
                </p>
                <p><strong>Has Insurance:</strong> 
                  <i
                    :class="selectedPatient.has_insurance === 'true' || selectedPatient.patient_type === 'insurance' 
                      ? 'fas fa-check text-success' 
                      : 'fas fa-times text-danger'"
                  ></i>
                </p>
                <p><strong>Visit Count:</strong> {{ selectedPatient.visit_count || 0 }}</p>
                <p><strong>Is Membership:</strong> 
                  <i
                    :class="selectedPatient.is_membership 
                      ? 'fas fa-star text-warning' 
                      : 'fas fa-star text-muted'"
                  ></i>
                </p>
              </b-col>
            </b-row>
          </b-card-body>
        </b-card>
      </div>
    </b-modal>

    <ToastNotification ref="toast" />
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BTable,
  BButton,
  BModal,
  BDropdown,
  BDropdownItem,
  BBadge,
  BMedia,
  BSpinner,
  BCardHeader,
  BCardBody,
  VBTooltip,
} from 'bootstrap-vue'

import Treeselect from '@riophae/vue-treeselect'
import '@riophae/vue-treeselect/dist/vue-treeselect.css'

// Components
import FilterReleasePatients from './filterReleasePatients.vue'
import ToastNotification from '@/components/ToastNotification'
import SpinnerLoading from '@/components/SpinnerLoading'
import PaginationTable from '@/components/PaginationTable'
import TitleTable from '@/components/TitleTable.vue'

// Services
import releasePatientService from '@/core/services/api/patient/releasePatient'

// Utils
import * as XLSX from 'xlsx/xlsx.mjs'
import Swal from 'sweetalert2'

export default {
  name: 'ReleasePatient',
  components: {
    BCard,
    BRow,
    BCol,
    BTable,
    BButton,
    BModal,
    BDropdown,
    BDropdownItem,
    BBadge,
    BMedia,
    BSpinner,
    BCardHeader,
    BCardBody,
    FilterReleasePatients,
    ToastNotification,
    SpinnerLoading,
    PaginationTable,
    TitleTable,
    Treeselect,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  data() {
    return {
      // Loading states
      loading: false,
      
      // Patients data
      patients: [],
      totalPatients: 0,
      
      // Pagination
      currentPage: 1,
      perPage: 10,
      dataMeta: { from: 1, to: 1, of: 1 },
      
      // Filters
      isFiltered: false,
      currentFilters: null,
      
      // Table configuration
      tableFields: [
        { key: 'id', label: 'ID', sortable: true },
        { key: 'patient', label: 'Patient', sortable: true },
        { key: 'phone', label: 'Phone', sortable: true },
        { key: 'date_birth', label: 'DOB', sortable: true },
        { key: 'visit_count', label: 'Visits', sortable: true },
        { key: 'patient_type', label: 'Type', sortable: true },
        { key: 'has_insurance', label: 'Insurance', sortable: false },
        { key: 'status', label: 'Status', sortable: true },
        { key: 'actions', label: 'Actions', sortable: false },
      ],
      
      // Pagination options
      perPageOptions: [10, 25, 50, 100].map(i => ({
        id: i,
        label: `Show ${i} Entries`,
      })),
      
      // Modal states
      isReleaseModalActive: false,
      isDetailsModalActive: false,
      selectedPatient: null,
    }
  },
  
  mounted() {
    this.loadPatients()
  },
  
  methods: {
    // Load patients from API
    async loadPatients() {
      this.loading = true
      try {
        const response = await releasePatientService.getAllPatients(this.perPage, this.currentPage)
        
        this.patients = response.data
        this.totalPatients = response.total
        this.updateDataMeta(response)
        
      } catch (error) {
        
        this.$refs.toast.danger('Error loading patients. Please try again.')
      } finally {
        this.loading = false
      }
    },
    
    // Search with filters
    async searchFilter(filters) {
      this.loading = true
      this.currentFilters = filters
      
      try {
        let response
        
        // Check if we have search terms
        const hasSearchTerms = filters.name || filters.phone
        
        if (hasSearchTerms) {
          // Use search API
          const searchType = filters.name ? 'name' : 'phone'
          const searchTerm = filters.name || filters.phone
          response = await releasePatientService.searchPatients(searchTerm, searchType, this.perPage)
        } else {
          // Use regular load with filters
          response = await releasePatientService.getAllPatients(this.perPage, this.currentPage)
        }
        
        // Apply client-side filters
        let filteredPatients = response.data
        
        if (filters.patient_type) {
          filteredPatients = filteredPatients.filter(p => p.patient_type === filters.patient_type)
        }
        
        if (filters.gender) {
          filteredPatients = filteredPatients.filter(p => p.gender === filters.gender)
        }
        
        if (filters.status !== null) {
          const statusBool = filters.status === 'true'
          filteredPatients = filteredPatients.filter(p => p.status === statusBool)
        }
        
        if (filters.has_insurance !== null) {
          const hasInsurance = filters.has_insurance === 'true'
          filteredPatients = filteredPatients.filter(p => 
            (p.has_insurance === 'true' || p.patient_type === 'insurance') === hasInsurance
          )
        }
        
        if (filters.visit_count) {
          filteredPatients = filteredPatients.filter(p => 
            (p.visit_count || 0) >= parseInt(filters.visit_count)
          )
        }
        
        if (filters.date_birth) {
          filteredPatients = filteredPatients.filter(p => 
            p.date_birth && p.date_birth.includes(filters.date_birth.replace(/-/g, '-'))
          )
        }
        
        this.patients = filteredPatients
        this.totalPatients = filteredPatients.length
        this.updateDataMeta({ 
          ...response, 
          data: filteredPatients, 
          total: filteredPatients.length 
        })
        
      } catch (error) {
        
        this.$refs.toast.danger('Error searching patients. Please try again.')
      } finally {
        this.loading = false
      }
    },
    
    // Handle filter page change
    filterPage(isFiltered) {
      this.isFiltered = isFiltered
      if (!isFiltered) {
        this.currentFilters = null
        this.loadPatients()
      }
    },
    
    // Handle pagination
    async pageChanged(page) {
      this.currentPage = page
      
      if (this.isFiltered && this.currentFilters) {
        await this.searchFilter(this.currentFilters)
      } else {
        await this.loadPatients()
      }
    },
    
    // Handle per page change
    async onPerPageChange() {
      this.currentPage = 1
      
      if (this.isFiltered && this.currentFilters) {
        await this.searchFilter(this.currentFilters)
      } else {
        await this.loadPatients()
      }
    },
    
    // Refresh data
    async refreshData() {
      if (this.isFiltered && this.currentFilters) {
        await this.searchFilter(this.currentFilters)
      } else {
        await this.loadPatients()
      }
    },
    
         // Show release modal
     showReleaseModal(patient) {
       this.selectedPatient = patient
       this.isReleaseModalActive = true
     },
    
         // Confirm release patient
     async confirmReleasePatient() {
       if (!this.selectedPatient) {
         this.$refs.toast.warning('Please select a patient first.')
         return
       }
       
       try {
         await releasePatientService.releasePatient(
           this.selectedPatient.id,
           this.selectedPatient.patient_type
         )
         
         this.$refs.toast.success(`Patient ${this.selectedPatient.full_name} has been released successfully.`)
         
         // Reset modal
         this.isReleaseModalActive = false
         this.selectedPatient = null
         
         // Refresh data
         await this.refreshData()
         
       } catch (error) {
         
         this.$refs.toast.danger('Error releasing patient. Please try again.')
       }
     },
    
    // View patient details
    viewPatientDetails(patient) {
      this.selectedPatient = patient
      this.isDetailsModalActive = true
    },
    
    // View patient history
    viewPatientHistory(patient) {
      this.$refs.toast.info(`Viewing history for ${patient.full_name}`)
      // TODO: Implement history view
    },
    
    // Generate patient report
    generatePatientReport(patient) {
      try {
        // Create Excel report
        const wb = XLSX.utils.book_new()
        
        // Patient info sheet
        const patientData = [
          ['Patient Release Report', ''],
          ['Generated On', new Date().toLocaleDateString()],
          ['', ''],
          ['Patient Information', ''],
          ['ID', patient.id],
          ['Name', patient.full_name],
          ['Phone', patient.phone || 'N/A'],
          ['Email', patient.email || 'N/A'],
          ['Date of Birth', patient.date_birth || 'N/A'],
          ['Gender', patient.gender || 'N/A'],
          ['Patient Type', patient.patient_type],
          ['Status', patient.status ? 'Active' : 'Inactive'],
          ['Has Insurance', patient.has_insurance === 'true' || patient.patient_type === 'insurance' ? 'Yes' : 'No'],
          ['Visit Count', patient.visit_count || 0],
          ['Is Membership', patient.is_membership ? 'Yes' : 'No'],
        ]
        
        const ws = XLSX.utils.aoa_to_sheet(patientData)
        XLSX.utils.book_append_sheet(wb, ws, 'Patient Info')
        
        // Save file
        const filename = `Patient_Report_${patient.id}_${new Date().toISOString().split('T')[0]}.xlsx`
        XLSX.writeFile(wb, filename)
        
        this.$refs.toast.success('Patient report generated successfully!')
        
      } catch (error) {
        
        this.$refs.toast.danger('Error generating report. Please try again.')
      }
    },
    
    // Export all patients to Excel
    exportExcel() {
      try {
        if (this.patients.length === 0) {
          this.$refs.toast.warning('No patients to export.')
          return
        }
        
        // Prepare data for export
        const exportData = this.patients.map(patient => ({
          ID: patient.id,
          Name: patient.full_name,
          Phone: patient.phone || '',
          Email: patient.email || '',
          DOB: patient.date_birth || '',
          Gender: patient.gender || '',
          'Patient Type': patient.patient_type,
          Status: patient.status ? 'Active' : 'Inactive',
          'Has Insurance': patient.has_insurance === 'true' || patient.patient_type === 'insurance' ? 'Yes' : 'No',
          'Visit Count': patient.visit_count || 0,
          'Is Membership': patient.is_membership ? 'Yes' : 'No',
        }))
        
        // Create Excel workbook
        const wb = XLSX.utils.book_new()
        const ws = XLSX.utils.json_to_sheet(exportData)
        XLSX.utils.book_append_sheet(wb, ws, 'Patients')
        
        // Save file
        const filename = `Release_Patients_${new Date().toISOString().split('T')[0]}.xlsx`
        XLSX.writeFile(wb, filename)
        
        this.$refs.toast.success('Patients exported successfully!')
        
      } catch (error) {
        
        this.$refs.toast.danger('Error exporting data. Please try again.')
      }
    },
    
    // Utility methods
    formatPhone(phone) {
      if (!phone) return ''
      return phone
        .toString()
        .replace(/\D+/g, '')
        .replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3')
    },
    
    formatDate(date) {
      if (!date) return ''
      try {
        return new Date(date).toLocaleDateString()
      } catch {
        return date
      }
    },
    
    updateDataMeta(response) {
      this.dataMeta = {
        from: ((response.current_page - 1) * response.per_page) + 1,
        to: Math.min(response.current_page * response.per_page, response.total),
        of: response.total,
      }
    },
  },
}
</script>

<style scoped>
.badge-glow {
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
}

.w-25 {
  width: 25% !important;
}

.patient-info-card {
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
  padding: 1rem;
  margin-bottom: 1rem;
}

.patient-info-card .mb-2:last-child {
  margin-bottom: 0 !important;
}

@media (max-width: 768px) {
  .w-25 {
    width: 100% !important;
  }
}
</style> 